// Implements: F-UIX-019, F-UIX-007, F-GEN-036
/**
 * The platform's model settings (MS-07 contract, see `types/modelSettings.ts`):
 * `GET` and `PUT /api/admin/model-settings`, `POST .../restore-env` and `GET .../revisions`.
 *
 * Only the platform admin holding `model_settings:manage` is answered (anybody else gets `403` or
 * `404`), which is why the page mounts this section on that action alone. The two writes change
 * what every later paid turn costs, so each is sent at most once (`retry: false`): a lost answer
 * is read again by the admin, never repeated behind their back (checklist 13). A `409` means
 * somebody saved first and is the caller's to show.
 */

import { httpRequest } from "../../../shared/api/httpClient";
import type {
  EstimateRequest,
  EstimateView,
  ModelSettingsRequest,
  ModelSettingsView,
  RestoreEnvRequest,
  RevisionsView,
} from "../../../types/modelSettings";

export const MODEL_SETTINGS_PATH = "/api/admin/model-settings";
export const RESTORE_ENV_PATH = `${MODEL_SETTINGS_PATH}/restore-env`;
export const ESTIMATE_PATH = `${MODEL_SETTINGS_PATH}/estimate`;
export const REVISIONS_PATH = `${MODEL_SETTINGS_PATH}/revisions`;

/** How many revisions the history lists. */
export const REVISIONS_LIMIT = 20;

async function body<T>(path: string, options: Parameters<typeof httpRequest>[1]): Promise<T> {
  const answer = await httpRequest<T>(path, options);
  if (answer === undefined) throw new Error("The model settings answered no body.");
  return answer;
}

export function fetchModelSettings(signal?: AbortSignal): Promise<ModelSettingsView> {
  return body(MODEL_SETTINGS_PATH, { signal });
}

export function saveModelSettings(request: ModelSettingsRequest): Promise<ModelSettingsView> {
  return body(MODEL_SETTINGS_PATH, { method: "PUT", body: request, retry: false });
}

export function restoreEnvSettings(request: RestoreEnvRequest): Promise<ModelSettingsView> {
  return body(RESTORE_ENV_PATH, { method: "POST", body: request, retry: false });
}

/** A read: nothing is written and nothing is paid. Still sent once (the dialog shows a fallback on failure). */
export function estimateModelSettings(request: EstimateRequest): Promise<EstimateView> {
  return body(ESTIMATE_PATH, { method: "POST", body: request, retry: false });
}

export function fetchRevisions(signal?: AbortSignal): Promise<RevisionsView> {
  return body(`${REVISIONS_PATH}?limit=${String(REVISIONS_LIMIT)}`, { signal });
}
